import React, { useEffect, useId, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import Checkbox from 'components/DesignSystem/Checkbox';
import Icon from 'components/DesignSystem/Icon';
import SecaoStatusPill from 'components/Relatorios/shared/Aprovacao/SecaoStatusPill';
import { autoriaDaSecao, formatarDataHoraCompacta } from 'helper/Relatorios/Aprovacao';
import { RelatorioAprovacaoSecao } from 'helper/Relatorios/Aprovacao/config';
import { AprovacaoSecao } from 'types/Relatorios/Aprovacao';

import './index.scss';

const BLOCO = 'relatorio-aprovacao-secoes-lista-mobile';

export interface SecoesListaMobileProps {
  /** `config.secoes`: a ordem das abas. */
  secoes: RelatorioAprovacaoSecao[];
  statusPorSlug: Record<string, AprovacaoSecao | undefined>;
  base: string;
  publicado: boolean;
  mutando: boolean;
  /** Seção cuja última ação falhou; `null` quando não há erro de linha. */
  secaoComErro: string | null;
  onToggleSecao: (slug: string) => (event: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Lista de seções do card "Aprovação do informe" no mobile (<768px): substitui a tabela do
 * desktop. Apresentacional: o PainelAprovacao decide o que aprovar/revogar e rastreia o erro.
 */
export default function SecoesListaMobile({
  secoes, statusPorSlug, base, publicado, mutando, secaoComErro, onToggleSecao,
}: SecoesListaMobileProps) {
  const idBase = useId();
  // Estado só de UI: qual linha foi tocada, para o spinner aparecer no lugar do toque.
  const [salvandoSlug, setSalvandoSlug] = useState<string | null>(null);

  useEffect(() => {
    if (!mutando) setSalvandoSlug(null);
  }, [mutando]);

  return (
    <ul className={BLOCO} aria-label="Seções do informe">
      {secoes.map(({ slug, label }) => {
        const secao = statusPorSlug[slug];
        if (!secao) return null;

        const aprovada = secao.status === 'aprovada';
        const autoria = autoriaDaSecao(secao);
        const salvando = mutando && salvandoSlug === slug;
        const temErro = secaoComErro === slug;
        const idStatus = `${idBase}-${slug}-status`;
        const idMeta = `${idBase}-${slug}-meta`;
        const idErro = `${idBase}-${slug}-erro`;
        const classes = [
          `${BLOCO}__item`,
          salvando ? `${BLOCO}__item--salvando` : '',
          temErro ? `${BLOCO}__item--erro` : '',
        ].filter(Boolean).join(' ');

        return (
          <li key={slug} className={classes} aria-busy={salvando || undefined}>
            <span className={`${BLOCO}__item-check-zona`}>
              <Checkbox
                className={`${BLOCO}__item-check`}
                checked={aprovada}
                disabled={publicado || mutando}
                onChange={(event) => {
                  setSalvandoSlug(slug);
                  onToggleSecao(slug)(event);
                }}
                aria-label={`${aprovada ? 'Revogar aprovação de' : 'Aprovar'} ${label}`}
                aria-describedby={temErro ? idErro : undefined}
              />
              {salvando && (
                <Spinner
                  animation="border"
                  size="sm"
                  aria-hidden="true"
                  className={`${BLOCO}__item-spinner`}
                />
              )}
            </span>

            <div className={`${BLOCO}__item-corpo`}>
              <Link
                to={`${base}/${slug}`}
                className={`${BLOCO}__item-link`}
                aria-describedby={autoria ? `${idStatus} ${idMeta}` : idStatus}
              >
                {label}
              </Link>
              <div className={`${BLOCO}__item-status`} id={idStatus}>
                <SecaoStatusPill status={secao.status} />
                {publicado && (
                  <span className={`${BLOCO}__item-publicado`}>
                    <span aria-hidden="true" className={`${BLOCO}__item-publicado-icone`}>
                      <Icon iconType="locked" size="small" color="currentColor" />
                    </span>
                    Publicado
                  </span>
                )}
              </div>
              {autoria && (
                <span className={`${BLOCO}__item-meta`} id={idMeta}>
                  {`${autoria.acao === 'aprovada' ? 'Aprovada' : 'Editado'} por ${autoria.por} · ${formatarDataHoraCompacta(autoria.em)}`}
                </span>
              )}
              {temErro && (
                <span className={`${BLOCO}__item-erro`} id={idErro}>
                  <span aria-hidden="true" className={`${BLOCO}__item-erro-icone`}>
                    <Icon iconType="warning" size="small" color="currentColor" />
                  </span>
                  Não foi possível salvar. Tente novamente.
                </span>
              )}
            </div>

            <span aria-hidden="true" className={`${BLOCO}__item-chevron`}>
              <Icon iconType="right_arrow" color="currentColor" />
            </span>
          </li>
        );
      })}
    </ul>
  );
}
